import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/common/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { uploadFileToDmsFolder } from '../../services/fileManagement';
import { focusFirstInvalid, validateRequired, type RequiredErrors, type RequiredFieldSpec } from '../../utils/requiredValidation';
import { setFlashToast } from '../../utils/flashToast';
import { digitsOnlyMax10, isValidNicOrPassport } from '../../utils/userFieldValidation';
import { getInternalUserProfileById, updateInternalUserProfile, type InternalUserProfile } from '../../services/userProfile';
import { AUTH_STORAGE_EVENT, getStorage } from '../../utils/authStorage';
import { validateProfileImageFile } from '../../utils/profileImageUpload';
import { SystemInfoBox, SystemInformationSection } from '../../components/ui/SystemInformation';
import { useDmsImageObjectUrl } from '../../hooks/useDmsImageObjectUrl';

export default function UserProfilePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [appUserId, setAppUserId] = useState(() => {
    const stored = getStorage<{ id?: string }>('appUser');
    return (stored?.id ?? '').trim();
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const trimmedAppUserId = appUserId.trim();
  const [isFetchingProfile, setIsFetchingProfile] = useState(() => Boolean(trimmedAppUserId));
  const loadingProfile = Boolean(trimmedAppUserId) && isFetchingProfile;
  const [profile, setProfile] = useState<InternalUserProfile | null>(null);
  const remoteProfilePicUrl = useDmsImageObjectUrl(
    profile?.profilePictureIconUrl,
    profile?.profilePictureIconUrlFullpath,
  );
  const [profilePreviewUrl, setProfilePreviewUrl] = useState<string>('');
  const [profilePicFileRef, setProfilePicFileRef] = useState<string>('');
  const [profilePicFileChanged, setProfilePicFileChanged] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const savingRef = useRef(false);
  const [editable, setEditable] = useState(() => ({
    nicOrPassport: '',
    serviceId: '',
    contactNumber: '',
  }));
  const [reqErrors, setReqErrors] = useState<RequiredErrors>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  useEffect(() => {
    return () => {
      if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl);
    };
  }, [profilePreviewUrl]);

  useEffect(() => {
    const sync = () => {
      const stored = getStorage<{ id?: string }>('appUser');
      const next = (stored?.id ?? '').trim();
      setAppUserId(next);
    };
    sync();
    window.addEventListener(AUTH_STORAGE_EVENT, sync as EventListener);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(AUTH_STORAGE_EVENT, sync as EventListener);
      window.removeEventListener('storage', sync);
    };
  }, []);

  useEffect(() => {
    // Only call profile-by-id once we have the backend appUserId in storage.
    // During auth transitions there can be a brief window where `user.id` is still a Keycloak subject.
    const id = appUserId.trim();
    if (!id) return;

    let alive = true;
    void (async () => {
      setIsFetchingProfile(true);
      setLoadError(null);
      try {
        const r = await getInternalUserProfileById(id);
        if (!alive) return;
        if (!r.success) {
          setLoadError(r.message ?? t('profile.loadFailed'));
          return;
        }
        setProfile(r.data);
        setEditable({
          nicOrPassport: r.data.nic ?? '',
          serviceId: r.data.serviceId ?? '',
          contactNumber: r.data.contactNumber ?? '',
        });
      } catch {
        if (!alive) return;
        setLoadError(t('profile.loadFailed'));
      } finally {
        if (alive) setIsFetchingProfile(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [appUserId, t]);

  const requiredSpecs: RequiredFieldSpec[] = [
    { key: 'nicOrPassport', label: t('profile.nicPassport'), value: editable.nicOrPassport, elementId: 'profile-nicOrPassport' },
  ];

  function clearReqError(key: string) {
    setReqErrors((cur) => {
      if (!cur[key]) return cur;
      const next = { ...cur };
      delete next[key];
      return next;
    });
  }

  async function handleSave() {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);

    try {
      const errs = validateRequired(requiredSpecs);
      const merged: RequiredErrors = { ...errs };
      const maxExceeded = t('profile.maxCharLimit');

      if (editable.serviceId.trim().length > 255) merged.serviceId = maxExceeded;
      if (!isValidNicOrPassport(editable.nicOrPassport)) {
        merged.nicOrPassport = t('profile.invalidNicPassport');
      }

      if (Object.keys(merged).length) {
        setReqErrors(merged);
        focusFirstInvalid(requiredSpecs, merged);
        return;
      }

      const r = await updateInternalUserProfile({
        nic: editable.nicOrPassport.trim(),
        serviceId: editable.serviceId.trim(),
        contactNumber: editable.contactNumber.trim() ? digitsOnlyMax10(editable.contactNumber) : '',
        profilePicFileRef: profilePicFileRef.trim(),
        profilePicFileChanged,
        existingProfilePicRef: profile?.profilePictureIconUrl,
      });

      if (!r.success) {
        setFlashToast({ message: r.message ?? t('profile.saveFailed'), variant: 'error' });
        return;
      }

      setFlashToast({ message: t('profile.saveSuccess'), variant: 'success' });
      setProfilePicFileChanged(false);

      const id = appUserId.trim();
      if (!id) return;
      setIsFetchingProfile(true);
      try {
        const rr = await getInternalUserProfileById(id);
        if (rr.success) {
          setProfile(rr.data);
          setEditable({
            nicOrPassport: rr.data.nic ?? '',
            serviceId: rr.data.serviceId ?? '',
            contactNumber: rr.data.contactNumber ?? '',
          });
        }
      } finally {
        setIsFetchingProfile(false);
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  return (
    <>
      <div className="bc">
            <span>{t('profile.breadcrumb')}</span>
      </div>

      <div className="cr-table-wrap" style={{ overflow: 'hidden' }}>
            <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--bl)', fontWeight: 800 }}>{t('profile.title')}</div>

        <div style={{ padding: 18 }}>
          {loadingProfile ? <div style={{ color: 'var(--t2)', marginBottom: 12 }}>{t('profile.loadingProfile')}</div> : null}
          {loadError ? (
            <div style={{ color: 'var(--red)', marginBottom: 12, fontWeight: 600, fontSize: 13 }} role="alert">
              {loadError}
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 18 }}>
            <div
              style={{
                width: 58,
                height: 58,
                borderRadius: 999,
                border: '1px solid var(--border)',
                background: 'var(--bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
              aria-hidden="true"
            >
              {profilePreviewUrl ? (
                <img src={profilePreviewUrl} alt={t('profile.profileImageAlt')} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : remoteProfilePicUrl ? (
                <img src={remoteProfilePicUrl} alt={t('profile.profileImageAlt')} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" aria-hidden="true">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.currentTarget.files?.[0] ?? null;
                if (!f) return;
                const validation = validateProfileImageFile(f);
                if (!validation.ok) {
                  setFlashToast({ message: validation.message, variant: 'error' });
                  e.currentTarget.value = '';
                  return;
                }

                if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl);
                const nextPreview = URL.createObjectURL(f);
                setProfilePreviewUrl(nextPreview);

                setUploading(true);
                void (async () => {
                  try {
                    const res = await uploadFileToDmsFolder(f);
                    if (!res.success) {
                      setFlashToast({ message: res.message ?? t('profile.uploadFailed'), variant: 'error' });
                      URL.revokeObjectURL(nextPreview);
                      setProfilePreviewUrl('');
                      return;
                    }
                    setProfilePicFileRef(res.data.file_name);
                    setProfilePicFileChanged(true);
                  } catch {
                    setFlashToast({ message: t('profile.uploadFailed'), variant: 'error' });
                    URL.revokeObjectURL(nextPreview);
                    setProfilePreviewUrl('');
                  }
                })()
                  .finally(() => {
                    setUploading(false);
                  });
              }}
            />

            <button
              className="mfb"
              type="button"
              aria-label={t('profile.upload')}
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              title={uploading ? t('profile.uploading') : t('profile.uploadImage')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </button>
            <button
              className="mfb"
              type="button"
              aria-label={t('profile.delete')}
              style={{ background: 'color-mix(in srgb, var(--red) 10%, var(--surface))' }}
              onClick={() => {
                if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl);
                setProfilePreviewUrl('');
                if (fileInputRef.current) fileInputRef.current.value = '';
                setProfilePicFileRef('');
                setProfilePicFileChanged(true);
                setProfile((cur) =>
                  cur
                    ? { ...cur, profilePictureIconUrl: '', profilePictureIconUrlFullpath: '' }
                    : cur,
                );
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
            </button>

            <span className="pill p-com" style={{ marginLeft: 8 }}>
                  {profile?.active ? t('common.active') : t('common.inactive')}
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="al-f">
                  <div className="al-l">{t('profile.name')}</div>
              <input className="al-inp" value={profile?.userDisplayName || user?.displayName || ''} disabled />
            </div>

            <div className="al-f">
                  <div className="al-l">{t('profile.emailUsername')}</div>
              <input className="al-inp" value={profile?.username || ''} disabled />
                  <div style={{ fontSize: 11, color: 'var(--t3)' }}>{t('profile.emailHint')}</div>
            </div>

            <div className="al-f">
              <div className="al-l">{t('profile.userRoles')}</div>
              <input className="al-inp" value={profile?.rolesDisplay || profile?.roleName || ''} disabled readOnly />
            </div>

            <div className="al-f">
                  <div className="al-l">{t('profile.divisionUnit')}</div>
              <input className="al-inp" value={profile?.divisionName || ''} disabled />
            </div>

            <div className="al-f">
              <div className="al-l">
                    {t('profile.nicPassport')} <span className="req">*</span>
              </div>
              <input
                id="profile-nicOrPassport"
                className="al-inp"
                value={editable.nicOrPassport}
                maxLength={15}
                onChange={(e) => {
                  const v = e.target.value;
                  setEditable((cur) => ({ ...cur, nicOrPassport: v }));
                  if (v.trim()) clearReqError('nicOrPassport');
                }}
                style={reqErrors.nicOrPassport ? { borderColor: 'var(--red)' } : undefined}
                aria-invalid={reqErrors.nicOrPassport ? true : undefined}
                aria-describedby={reqErrors.nicOrPassport ? 'profile-nicOrPassport-err' : undefined}
              />
              {reqErrors.nicOrPassport ? (
                <div id="profile-nicOrPassport-err" style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }}>
                  {reqErrors.nicOrPassport}
                </div>
              ) : null}
            </div>

            <div className="al-f">
                  <div className="al-l">{t('profile.designation')}</div>
              <input className="al-inp" value={profile?.designation || ''} disabled />
            </div>

            <div className="al-f">
                  <div className="al-l">{t('profile.serviceId')}</div>
              <input
                id="profile-serviceId"
                className="al-inp"
                value={editable.serviceId}
                maxLength={255}
                onChange={(e) => {
                  const v = e.target.value.slice(0, 255);
                  setEditable((cur) => ({ ...cur, serviceId: v }));
                  if (reqErrors.serviceId) clearReqError('serviceId');
                }}
                style={reqErrors.serviceId ? { borderColor: 'var(--red)' } : undefined}
                aria-invalid={reqErrors.serviceId ? true : undefined}
                aria-describedby={reqErrors.serviceId ? 'profile-serviceId-err' : undefined}
              />
              {reqErrors.serviceId ? (
                <div id="profile-serviceId-err" style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }}>
                  {reqErrors.serviceId}
                </div>
              ) : null}
            </div>

            <div className="al-f">
                  <div className="al-l">{t('profile.contactNumber')}</div>
              <input
                id="profile-contactNumber"
                className="al-inp"
                value={editable.contactNumber}
                maxLength={10}
                placeholder={t('profile.contactPlaceholder')}
                onChange={(e) => setEditable((cur) => ({ ...cur, contactNumber: digitsOnlyMax10(e.target.value) }))}
              />
            </div>
          </div>

          <SystemInformationSection title={t('profile.systemInformation')}>
            <SystemInfoBox
              label={t('profile.lastUpdatedAt')}
              value={profile?.updatedAt ? new Date(profile.updatedAt).toLocaleString() : undefined}
            />
            <SystemInfoBox label={t('profile.lastUpdatedBy')} value={profile?.updatedByName} />
          </SystemInformationSection>
        </div>

        <div className="tft" style={{ background: 'color-mix(in srgb, var(--bg) 60%, var(--surface))' }}>
          <div />
          <div className="pgr">
            <button className="mfb" type="button" disabled={saving} onClick={() => setCancelConfirmOpen(true)}>
              {t('common.cancel')}
            </button>
            <Button
              variant="primary"
              disabled={saving || uploading || loadingProfile}
              onClick={() => {
                void handleSave();
              }}
            >
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title={t('admin.shared.cancelChanges')}
        message={t('admin.shared.cancelChangesMessage')}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          setCancelConfirmOpen(false);
          nav(-1);
        }}
      />
    </>
  );
}


